import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

export type PlayerTransactionsPayload = Record<string, unknown>;

/** Normalize GET /transactions/by-player response into a list of transaction rows. */
export function getTransactionRowsFromPayload(payload: unknown): Record<string, unknown>[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }
  if (typeof payload !== 'object') return [];
  const o = payload as Record<string, unknown>;
  if (Array.isArray(o.transactions)) return o.transactions as Record<string, unknown>[];
  if (o.data && typeof o.data === 'object') {
    const d = o.data as Record<string, unknown>;
    if (Array.isArray(d.transactions)) return d.transactions as Record<string, unknown>[];
    if (Array.isArray(d.rows)) return d.rows as Record<string, unknown>[];
  }
  if (Array.isArray(o.rows)) return o.rows as Record<string, unknown>[];
  return [];
}

export interface UsePlayerTransactionsOptions {
  leagueId: number | undefined;
  /** dom_rosters.id — matches backend player_id / players[] on dom_transactions. */
  rosterPlayerId: number | undefined;
  /** Optional override for viewer team (can_see); backend falls back to auth user team. */
  myTeam?: number;
  page?: number;
  pageSize?: number;
  hoursBack?: number;
  type?: string;
  teamIds?: number[];
  enabled?: boolean;
}

export function usePlayerTransactions({
  leagueId,
  rosterPlayerId,
  myTeam,
  page = 0,
  pageSize = 25,
  hoursBack,
  type,
  teamIds,
  enabled = true,
}: UsePlayerTransactionsOptions) {
  return useQuery({
    queryKey: [
      'playerTransactions',
      leagueId,
      rosterPlayerId,
      myTeam,
      page,
      pageSize,
      hoursBack,
      type,
      teamIds,
    ],
    queryFn: async () => {
      if (leagueId == null || rosterPlayerId == null) {
        throw new Error('leagueId and rosterPlayerId are required');
      }
      const res = await apiClient.get<PlayerTransactionsPayload>(
        `/transactions/by-player/${leagueId}/${rosterPlayerId}`,
        {
          params: {
            page,
            pageSize,
            ...(myTeam != null && Number.isFinite(myTeam) ? { myTeam } : {}),
            ...(hoursBack != null && hoursBack > 0 ? { hoursBack } : {}),
            ...(type != null && type !== '' ? { type } : {}),
            ...(teamIds != null && teamIds.length > 0
              ? { teamIds: teamIds.join(',') }
              : {}),
          },
        }
      );
      return res.data;
    },
    enabled: enabled && leagueId != null && rosterPlayerId != null,
    staleTime: 30_000,
  });
}
